import React, { useState, useMemo } from 'react';
import { Box, Button, Stack, Typography, useTheme } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { IconSettings } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { BorderedGrid } from '@shared/components/borderedGrid';
import { CustomSuccessToast } from '@shared/components/customSuccessToast';
import { ToolTypeEnum } from '@shared/constants/campaign.constants';
import {
  useToolCreateControllerCreateCampaignToolMutation,
  useToolUpdateControllerCreateCampaignToolMutation,
  useToolDeleteControllerCreateCampaignToolMutation,
  ToolDto,
  ToolRequestDto,
} from '@shared/services/apiService/apiService';
import {
  McpToolsSectionProps,
  McpToolFormData,
  McpToolRawJson,
  toolDtoToFormData,
  toolDtoToRawJson,
  headersArrayToObject,
} from './mcp.types';
import McpToolCard from './McpToolCard';
import McpDeleteConfirmDialog from './McpDeleteConfirmDialog';
import McpToolPopup from './McpToolPopup';

const McpToolsSection: React.FC<McpToolsSectionProps> = ({
  campaignId,
  agents,
  tools,
  refetch,
  isCampaignActive,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();

  const [createCampaignTool] = useToolCreateControllerCreateCampaignToolMutation();
  const [updateCampaignTool] = useToolUpdateControllerCreateCampaignToolMutation();
  const [deleteCampaignTool] = useToolDeleteControllerCreateCampaignToolMutation();

  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [editingTool, setEditingTool] = useState<ToolDto | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [toolToDelete, setToolToDelete] = useState<string | null>(null);

  const mcpTools = useMemo(() => tools.filter((tool) => tool.type === ToolTypeEnum.Mcp), [tools]);

  const existingTools = useMemo(
    () =>
      mcpTools
        .filter((tool) => tool.id !== editingTool?.id)
        .map((tool) => ({
          mcpName: tool.mcpName || '',
          agentId: tool.agentId || null,
          id: tool.id,
        }))
        .filter((tool) => Boolean(tool.mcpName)),
    [mcpTools, editingTool],
  );

  const handleAddNew = () => {
    setEditingTool(null);
    setIsPopupOpen(true);
  };

  const handleEdit = (tool: ToolDto) => {
    setEditingTool(tool);
    setIsPopupOpen(true);
  };

  const handleClosePopup = () => {
    setIsPopupOpen(false);
    setEditingTool(null);
  };

  const handleDeleteClick = (toolId: string) => {
    setToolToDelete(toolId);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!toolToDelete) return;

    setIsLoading(true);
    try {
      await deleteCampaignTool({
        id: campaignId,
        toolId: toolToDelete,
      }).unwrap();

      toast.success(CustomSuccessToast, {
        closeButton: false,
        autoClose: 5000,
        icon: false,
      });

      refetch();
    } catch (error) {
      console.error('Error deleting tool:', error);
      toast.error(t('mcpTools.deleteError'), {
        closeButton: false,
        autoClose: 5000,
      });
    } finally {
      setIsLoading(false);
      setDeleteDialogOpen(false);
      setToolToDelete(null);
    }
  };

  const handleSubmit = async (formData: McpToolFormData) => {
    setIsLoading(true);
    try {
      const headersObject = headersArrayToObject(formData.mcpServerConfig.headers);

      const mcpToolRequest: ToolRequestDto = {
        type: ToolTypeEnum.Mcp,
        isAsync: true,
        mcpName: formData.mcpName.trim(),
        agentId: formData.agentId || undefined,
        mcpServerConfig: {
          url: formData.mcpServerConfig.url.trim(),
          headers: headersObject,
          transportType: formData.mcpServerConfig.transportType,
          agentId: formData.agentId || undefined,
        },
      };

      if (formData.id) {
        // Update existing tool
        await updateCampaignTool({
          id: campaignId,
          toolId: formData.id,
          toolUpdateRequestDto: {
            isAsync: mcpToolRequest.isAsync,
            mcpName: mcpToolRequest.mcpName,
            agentId: mcpToolRequest.agentId,
            mcpServerConfig: mcpToolRequest.mcpServerConfig,
          },
        }).unwrap();
      } else {
        // Create new tool
        await createCampaignTool({
          id: campaignId,
          toolRequestDto: mcpToolRequest,
        }).unwrap();
      }

      toast.success(CustomSuccessToast, {
        closeButton: false,
        autoClose: 5000,
        icon: false,
      });

      setIsPopupOpen(false);
      setEditingTool(null);
      refetch();
    } catch (error) {
      console.error('Error saving tool:', error);
      toast.error(t('mcpTools.saveError'), {
        closeButton: false,
        autoClose: 5000,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmitRawJson = async (rawJson: McpToolRawJson) => {
    setIsLoading(true);
    try {
      const mcpToolRequest: ToolRequestDto = {
        type: ToolTypeEnum.Mcp,
        isAsync: true,
        mcpName: rawJson.mcpName.trim(),
        agentId: rawJson.agentId || undefined,
        mcpServerConfig: {
          ...rawJson.mcpServerConfig,
          agentId: rawJson.agentId || undefined,
        },
      };

      if (rawJson.id) {
        // Update existing tool
        await updateCampaignTool({
          id: campaignId,
          toolId: rawJson.id,
          toolUpdateRequestDto: {
            isAsync: mcpToolRequest.isAsync,
            mcpName: mcpToolRequest.mcpName,
            agentId: mcpToolRequest.agentId,
            mcpServerConfig: mcpToolRequest.mcpServerConfig,
          },
        }).unwrap();
      } else {
        // Create new tool
        await createCampaignTool({
          id: campaignId,
          toolRequestDto: mcpToolRequest,
        }).unwrap();
      }

      toast.success(CustomSuccessToast, {
        closeButton: false,
        autoClose: 5000,
        icon: false,
      });

      setIsPopupOpen(false);
      setEditingTool(null);
      refetch();
    } catch (error) {
      console.error('Error saving tool:', error);
      toast.error(t('mcpTools.saveError'), {
        closeButton: false,
        autoClose: 5000,
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Box sx={{ mt: 4 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
        <Stack
          direction="row"
          alignItems="center"
          spacing={1.5}
          sx={{
            svg: {
              color: theme.palette.getContrastText(theme.palette.background.default),
            },
          }}>
          <IconSettings size={22} />
          <Typography fontSize="18px" fontWeight={600}>
            {t('mcpTools.title')}
          </Typography>
        </Stack>
        <Button
          variant="contained"
          size="small"
          startIcon={<AddIcon />}
          onClick={handleAddNew}
          disabled={isCampaignActive}>
          {t('mcpTools.addTool')}
        </Button>
      </Stack>

      {mcpTools.length > 0 ? (
        <Box>
          {mcpTools.map((tool) => (
            <McpToolCard
              key={tool.id}
              tool={tool}
              agents={agents}
              onEdit={handleEdit}
              onDelete={handleDeleteClick}
              disabled={isCampaignActive || isLoading}
            />
          ))}
        </Box>
      ) : (
        <BorderedGrid
          sx={{
            padding: '40px 20px',
            textAlign: 'center',
            backgroundColor: theme.palette.surface.sunken,
          }}>
          <Typography color="text.secondary">{t('mcpTools.emptyState')}</Typography>
        </BorderedGrid>
      )}

      <McpToolPopup
        open={isPopupOpen}
        onClose={handleClosePopup}
        initialData={editingTool ? toolDtoToFormData(editingTool) : null}
        initialRawJson={editingTool ? toolDtoToRawJson(editingTool) : null}
        agents={agents}
        existingTools={existingTools}
        onSubmit={handleSubmit}
        onSubmitRawJson={handleSubmitRawJson}
        isLoading={isLoading}
        disabled={isCampaignActive}
      />

      <McpDeleteConfirmDialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        onConfirm={handleDeleteConfirm}
        isLoading={isLoading}
      />
    </Box>
  );
};

export default McpToolsSection;
